import { Link, useParams, useSearchParams } from "react-router-dom";
import { decodeFA } from "../utils/decodeFA.js";
import { cn } from "../utils/cn.js";
import { Popover } from "./Popover.js";
import {
  useResource,
  type ResourceRequest,
  type ResourceResponse,
  type ViewResourcesResponse
} from "@swivu/client-api";
import { buttonActions } from "./Button.js";
import { createContext, useContext } from "react";
import { useIsGummy } from "../hooks/useGummyTheme.js";

import BoardValues from "./BoardValues.js";
import SuspenseWithDelay from "./SuspenseWithDelay.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
import { tileForId } from "../utils/tilePalette.js";

type ResourceResult = ResourceResponse;

type ViewResourceVariables = ResourceRequest & {
  id: string;
  baseDetailUrl: string;
};
type ViewResourceProps = ViewResourceVariables & {
  resource: ResourceResponse | undefined;
};

const ViewResourceContext = createContext<ViewResourceProps | null>(null);

const ViewResourceProviderWrapper = ({
  children,
  ...props
}: ViewResourceVariables & { children: React.ReactNode }) => {
  return (
    // <SuspenseWithDelay fallback={<ViewResourceSkeleton />}>
    //   <ErrorBoundary>
    <ViewResourceProvider {...props}>{children}</ViewResourceProvider>
    //   </ErrorBoundary>
    // </SuspenseWithDelay>
  );
};

const ViewResourceProvider = ({
  children,
  ...props
}: ViewResourceVariables & { children: React.ReactNode }) => {
  const { data } = useResource({
    variables: {
      ...props
    }
  });

  return (
    <ViewResourceContext.Provider value={{ ...props, resource: data }}>
      {children}
    </ViewResourceContext.Provider>
  );
};

const useViewResource = () => {
  const context = useContext(ViewResourceContext);
  if (!context) {
    throw new Error(
      "useViewResource must be used within a ViewResourceProvider"
    );
  }
  return context;
};

type ResourceListType = ViewResourcesResponse[number];
type ViewResourceListProps =
  | { resource?: undefined; baseDetailUrl?: undefined }
  | { resource: ResourceListType; baseDetailUrl: string };

const ViewResource = ({ resource, baseDetailUrl }: ViewResourceListProps) => {
  if (resource) {
    return (
      <ViewResourceListUI resource={resource} baseDetailUrl={baseDetailUrl} />
    );
  }
  return <ViewResourceRouteUI />;
};

const ViewResourceListUI = ({
  resource,
  baseDetailUrl
}: {
  resource: ResourceListType;
  baseDetailUrl: string;
}) => {
  const isGummy = useIsGummy();
  return (
    <div
      key={resource.id}
      className={cn(
        "relative flex",
        isGummy
          ? ""
          : "border-mono-300 dark:border-mono-700/50 text-mono-700 dark:text-mono-300 hover:border-mono-400"
      )}
    >
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col gap-2 sm:flex-row md:gap-4",
          isGummy ? "py-5" : "py-4"
        )}
      >
        <div className="flex flex-1  gap-1 focus:outline-none">
          <div className="flex flex-1 gap-x-4 ">
            {resource?.icon ? (
              <div className="flex-shrink-0">
                <i
                  className={cn(
                    `fa-regular mt-1 ${decodeFA(resource?.icon.toString())}`,
                    isGummy
                      ? "text-4xl text-shadow-on-color"
                      : "mobile-pref-view-icon text-mono-500 dark:text-mono-300"
                  )}
                />
              </div>
            ) : null}

            <div className={"w-full [&>.has-tags]:pt-2"}>
              <div className="flex flex-col items-start justify-between  gap-1 pr-4 md:flex-row md:gap-x-4 [&>.has-status]:pb-4 ">
                <p
                  className={cn(
                    "mobile-pref-card-title",
                    isGummy
                      ? "font-display text-xl font-black tracking-tight text-shadow-on-color"
                      : "dark:text-mono-100 text-base font-medium"
                  )}
                >
                  {resource.label || resource.display_name}
                </p>
                {resource?.status && (
                  <div className={"has-status "}>
                    <span
                      style={isGummy ? { textShadow: "none" } : undefined}
                      className={cn(
                        "mobile-pref-card-meta flex items-center justify-center rounded-full md:opacity-0",
                        isGummy
                          ? "shadow-pill bg-white/95 text-mono-900 px-3 py-1 text-sm font-black uppercase tracking-wide md:px-4"
                          : "md:dark:bg-mono-950 border-mono-300 dark:border-mono-700/50 py-0.5 text-xs font-bold opacity-60 md:border md:bg-white md:px-3"
                      )}
                    >
                      {resource.status}
                    </span>
                  </div>
                )}
              </div>
              <ul className="grid grid-cols-2 flex-wrap  gap-x-6 gap-y-1.5 py-1 md:flex  md:gap-y-2">
                <ResourceSubItems resource={resource} />
              </ul>

              {resource?.tag && (
                <div
                  className={cn(
                    "has-tags mt-1 flex flex-wrap items-center  gap-x-2 md:gap-y-2",
                    !isGummy && "opacity-60"
                  )}
                >
                  <i
                    className={cn(
                      `fa-regular ${decodeFA(`tag`)}`,
                      isGummy
                        ? "text-xl text-shadow-on-color"
                        : "mobile-pref-view-inline-icon"
                    )}
                  />
                  <ul className="flex flex-wrap gap-x-2 gap-y-1 md:gap-y-2">
                    {resource.tag.map((tag) => (
                      <li
                        key={tag}
                        className={cn(
                          "mobile-pref-card-meta flex items-center gap-2",
                          isGummy
                            ? "bg-white/20 text-white text-shadow-on-color rounded-full px-3 py-1 text-sm font-bold"
                            : "border-mono-300 dark:border-mono-700 rounded-md border px-2 py-0.5 text-xs font-bold leading-4"
                        )}
                      >
                        <span>{tag}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {resource.assigned_boardusers.length > 0 && (
                <div
                  className={cn(
                    "flex flex-wrap items-center gap-x-2 md:gap-y-2",
                    !isGummy && "opacity-60"
                  )}
                >
                  <i
                    className={cn(
                      `fa-regular ${decodeFA(`users`)}`,
                      isGummy
                        ? "text-xl text-shadow-on-color"
                        : "mobile-pref-view-inline-icon"
                    )}
                  />
                  <ul className="flex flex-wrap gap-x-2 gap-y-1 md:gap-y-2">
                    {resource.assigned_boardusers.map((user) => (
                      <li
                        key={user}
                        className={cn(
                          "mobile-pref-card-meta flex items-center gap-2 leading-tight",
                          isGummy
                            ? "text-base font-bold opacity-80"
                            : "text-xs leading-4"
                        )}
                      >
                        <span>{user}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <div className="dark:border-mono-700/50 relative mt-1  flex flex-row-reverse flex-wrap  items-start justify-end  gap-3  md:mt-0 md:flex-row md:items-end">
            {resource?.status && (
              <div
                className={
                  "has-status absolute right-0 top-0 hidden whitespace-nowrap md:block"
                }
              >
                <span
                  style={isGummy ? { textShadow: "none" } : undefined}
                  className={cn(
                    "flex items-center justify-center rounded-full md:px-4",
                    isGummy
                      ? "shadow-pill bg-white/95 text-mono-900 px-3 py-1 text-sm font-black uppercase tracking-wide"
                      : "dark:bg-mono-950 border-mono-300 dark:border-mono-700/50 bg-white py-0.5 text-xs font-bold opacity-60 md:border md:px-3"
                  )}
                >
                  {resource.status}
                </span>
              </div>
            )}

            <Link
              viewTransition={true}
              to={`${baseDetailUrl}/${resource.id}?title=${resource.label}`}
              style={isGummy ? { textShadow: "none" } : undefined}
              className={cn(
                "relative transition-all active:scale-95",
                isGummy
                  ? cn(
                      "inline-flex h-12 w-12 items-center justify-center rounded-full",
                      "shadow-pill bg-white/95 text-mono-900",
                      "hover:-translate-y-0.5 motion-reduce:transform-none",
                      "outline-none focus-visible:ring-4 focus-visible:ring-white/80"
                    )
                  : cn(
                      "mobile-pref-control",
                      `rounded-md px-6 py-2 text-lg ${buttonActions.secondary.default} dark:bg-mono-950 text-mono-500`,
                      "border-mono-300 dark:border-mono-700/50 border bg-white",
                      "md:px-4 md:py-1"
                    )
              )}
              aria-label={`View ${resource.label || resource.display_name}`}
            >
              <i
                className={cn(
                  `fa-regular ${decodeFA(`circle-info`)}`,
                  isGummy ? "text-2xl" : "mobile-pref-view-icon"
                )}
              />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
const ViewResourceRouteUI = () => {
  const { resource } = useViewResource();
  const { resource_id } = useParams<{ resource_id: string }>();
  const [urlSearchParams] = useSearchParams();
  const title = urlSearchParams.get("title");
  const isGummy = useIsGummy();
  const tileKey = resource?.id ?? resource_id ?? "";

  return (
    <div
      key={resource?.id ?? resource_id}
      className={cn(
        "relative flex",
        isGummy
          ? cn(
              "overflow-hidden px-5 text-white text-shadow-on-color",
              "rounded-surface-lg",
              tileForId(tileKey),
              "shadow-card"
            )
          : "border-mono-300 dark:border-mono-700/50 text-mono-700 dark:text-mono-300 hover:border-mono-400"
      )}
    >
      {isGummy && <span aria-hidden className="top-gloss" />}
      <div
        className={cn(
          "flex min-w-0 flex-1 flex-col gap-2 sm:flex-row md:gap-4",
          isGummy ? "py-5" : "py-4"
        )}
      >
        <div className="flex flex-1  gap-1 focus:outline-none">
          <div className="flex flex-1 gap-x-4 ">
            {resource?.icon ? (
              <div className="flex-shrink-0">
                <i
                  className={cn(
                    `fa-regular mt-1 ${decodeFA(resource?.icon.toString())}`,
                    isGummy
                      ? "text-4xl text-shadow-on-color"
                      : "size-6 text-mono-500 dark:text-mono-300"
                  )}
                />
              </div>
            ) : null}

            <div className={"w-full [&>.has-tags]:pt-2"}>
              <div className="flex flex-col items-start justify-between  gap-1 pr-4 md:flex-row md:gap-x-4 [&>.has-status]:pb-4 ">
                <p
                  className={cn(
                    isGummy
                      ? "font-display text-xl font-black tracking-tight text-shadow-on-color"
                      : "dark:text-mono-100 text-base font-medium"
                  )}
                >
                  {resource?.label || resource?.display_name || title}
                </p>

                <div className={"has-status "}>
                  <span
                    style={isGummy ? { textShadow: "none" } : undefined}
                    className={cn(
                      "flex items-center justify-center rounded-full md:opacity-0",
                      isGummy
                        ? "shadow-pill bg-white/95 text-mono-900 px-3 py-1 text-sm font-black uppercase tracking-wide md:px-4"
                        : "dark:bg-mono-950 border-mono-300 dark:border-mono-700/50 py-0.5 text-xs font-bold opacity-60 md:border md:bg-white md:px-3"
                    )}
                  >
                    {resource?.status_value ?? ""}
                  </span>
                </div>
              </div>
              <ul className="grid grid-cols-2 flex-wrap  gap-x-6 gap-y-1.5 py-1 md:flex  md:gap-y-2">
                {resource && (
                  <SuspenseWithDelay fallback={<ResourceSubItemsSkeleton />}>
                    <ErrorBoundary>
                      <ResourceSubItems resource={resource} />
                    </ErrorBoundary>
                  </SuspenseWithDelay>
                )}
              </ul>

              {resource?.tag_values && (
                <div
                  className={cn(
                    "has-tags mt-1 flex flex-wrap items-center gap-x-2 md:gap-y-2",
                    !isGummy && "opacity-60"
                  )}
                >
                  <i
                    className={cn(
                      `fa-regular ${decodeFA(`tag`)}`,
                      isGummy ? "text-xl text-shadow-on-color" : "size-3"
                    )}
                  />
                  <ul className="flex flex-wrap gap-x-2 gap-y-1 md:gap-y-2">
                    {resource.tag_values.map((tag) => (
                      <li
                        key={tag}
                        className={cn(
                          "flex items-center gap-2",
                          isGummy
                            ? "bg-white/20 text-white text-shadow-on-color rounded-full px-3 py-1 text-sm font-bold"
                            : "border-mono-300 dark:border-mono-700 rounded-md border px-2 py-0.5 text-xs font-bold leading-3"
                        )}
                      >
                        <span>{tag}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {resource && resource?.assigned_boardusers?.length > 0 ? (
                <div
                  className={cn(
                    "flex flex-wrap items-center gap-x-2 md:gap-y-2",
                    !isGummy && "opacity-60"
                  )}
                >
                  <i
                    className={cn(
                      `fa-regular ${decodeFA(`users`)}`,
                      isGummy ? "text-xl text-shadow-on-color" : "size-3"
                    )}
                  />
                  <ul className="flex flex-wrap gap-x-2 gap-y-1 md:gap-y-2">
                    {resource.assigned_boardusers.map((user) => (
                      <li
                        key={user}
                        className={cn(
                          "flex items-center gap-2 leading-tight",
                          isGummy
                            ? "text-base font-bold opacity-80"
                            : "text-xs leading-3"
                        )}
                      >
                        <span>{user}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
          <div className="dark:border-mono-700/50 relative mt-1  flex flex-row-reverse flex-wrap  items-start justify-end  gap-3  md:mt-0 md:flex-row md:items-end">
            <div
              className={
                "has-status absolute right-0 top-0 hidden whitespace-nowrap md:block"
              }
            >
              <span
                style={isGummy ? { textShadow: "none" } : undefined}
                className={cn(
                  "flex items-center justify-center rounded-full md:px-4",
                  isGummy
                    ? "shadow-pill bg-white/95 text-mono-900 px-3 py-1 text-sm font-black uppercase tracking-wide"
                    : "dark:bg-mono-950 border-mono-300 dark:border-mono-700/50 bg-white py-0.5 text-xs font-bold opacity-60 md:border md:px-3"
                )}
              >
                {resource?.status_value ?? ""}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const subItems = [
  { field: "location", icon: "location-dot", label: "Location", api: null },
  { field: "type", icon: "symbols", label: "Type", api: "boardResourceTypes" },
  {
    field: "category",
    icon: "layer-group",
    label: "Category",
    api: "boardResourceCategories"
  },
  { field: "job_title", icon: "briefcase", label: "Job Title", api: null },
  { field: "team", icon: "users", label: "Team", api: null }
  // { field: "aging", icon: "clock", label: "Aging" }
] as const;

const ResourceSubItemsSkeleton = () => {
  return (
    <>
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="flex items-center gap-2">
          <div className="bg-mono-300 dark:bg-mono-700 h-4 w-4 rounded-full"></div>
          <div className="bg-mono-300 dark:bg-mono-700 h-4 w-20 rounded-full"></div>
        </div>
      ))}
    </>
  );
};
const ResourceSubItems = ({
  resource
}: {
  resource: ResourceListType | ResourceResult;
}) => {
  const isGummy = useIsGummy();
  return subItems
    .filter((item) => !!resource[item.field])
    .map((item) => {
      const resourceValue = resource[item.field];
      return (
        <Popover.Root key={item.field}>
          <Popover.Trigger asChild>
            <li
              className={cn(
                "group cursor-pointer leading-tight",
                isGummy ? "text-base" : "text-xs leading-4"
              )}
            >
              <button
                className={cn(
                  "mobile-pref-inline-button mobile-pref-card-meta flex items-center text-left transition-opacity group-hover:opacity-100",
                  isGummy
                    ? "gap-3 font-bold opacity-80"
                    : "gap-3 font-bold opacity-70"
                )}
              >
                <i
                  className={cn(
                    `fa-regular ${decodeFA(item.icon)}`,
                    isGummy
                      ? "text-xl text-shadow-on-color"
                      : "mobile-pref-view-inline-icon"
                  )}
                />
                {"board" in resource && resource.board && item?.api ? (
                  <BoardValues
                    valueIds={resourceValue}
                    route={item.api}
                    id={resource.board.toFixed()}
                  >
                    {(values) => <span>{values.join(", ")}</span>}
                  </BoardValues>
                ) : (
                  <span>{resourceValue}</span>
                )}
              </button>
            </li>
          </Popover.Trigger>
          <Popover.Content
            align="start"
            className="w-auto max-w-[300px] px-4 py-3 text-xs"
          >
            <div className="mobile-pref-card-meta space-y-0.5 font-bold">
              {"board" in resource && resource.board && item?.api ? (
                <BoardValues
                  valueIds={resourceValue}
                  route={item.api}
                  id={resource.board.toFixed()}
                >
                  {(values) => <div>{values.join(", ")}</div>}
                </BoardValues>
              ) : (
                <div>{resourceValue}</div>
              )}
              <div className="flex items-center gap-x-2 font-medium italic opacity-70">
                <i
                  className={`mobile-pref-view-inline-icon fa-regular ${decodeFA(item.icon)}`}
                />
                <span>{item.label}</span>
              </div>
            </div>
          </Popover.Content>
        </Popover.Root>
      );
    });
};

ViewResource.Provider = ViewResourceProviderWrapper;
ViewResource.useViewResource = useViewResource;
export default ViewResource;
